import React, { useState } from 'react';
import { Check, X, Search } from 'lucide-react';

interface MultiSelectFieldProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  helpText?: string;
  required?: boolean;
  error?: string;
}

export default function MultiSelectField({
  label,
  value = [],
  onChange,
  options,
  placeholder = 'Select options',
  helpText,
  required = false,
  error
}: MultiSelectFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const handleRemove = (optionValue: string) => {
    onChange(value.filter(v => v !== optionValue));
  };

  const handleSelectAll = () => {
    onChange(filteredOptions.map(opt => opt.value));
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const getSelectedLabels = () => {
    return value
      .map(v => options.find(opt => opt.value === v)?.label)
      .filter(Boolean);
  };

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {helpText && (
        <p className="text-xs text-gray-500">{helpText}</p>
      )}

      {/* Selected Items Display */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-md border border-gray-200">
          {getSelectedLabels().map((label, index) => (
            <span
              key={value[index]}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-sm"
            >
              {label}
              <button
                type="button"
                onClick={() => handleRemove(value[index])}
                className="hover:text-blue-900"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown Toggle */}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full px-3 py-2 text-left border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            error ? 'border-red-300' : 'border-gray-300'
          }`}
        >
          <span className="text-gray-500">
            {value.length === 0
              ? placeholder
              : `${value.length} selected`}
          </span>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown Content */}
            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col">
              {/* Search Bar */}
              <div className="p-2 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search options..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 p-2 border-b border-gray-200 bg-gray-50">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="flex-1 px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Select All ({filteredOptions.length})
                </button>
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="flex-1 px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Clear All
                </button>
              </div>

              {/* Options List */}
              <div className="overflow-y-auto flex-1">
                {filteredOptions.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500">
                    No options found
                  </div>
                ) : (
                  filteredOptions.map(option => (
                    <label
                      key={option.value}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={value.includes(option.value)}
                        onChange={() => handleToggle(option.value)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="flex-1 text-sm text-gray-700">
                        {option.label}
                      </span>
                      {value.includes(option.value) && (
                        <Check className="w-4 h-4 text-blue-600" />
                      )}
                    </label>
                  ))
                )}
              </div>

              {/* Footer */}
              <div className="p-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-600 text-center">
                {value.length} of {options.length} selected
              </div>
            </div>
          </>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}